import { useEffect, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { CheckCircle2, CircleAlert, X } from "lucide-react";

export interface ToastMessage {
  id: number;
  text: string;
  tone: "success" | "error";
}

const DRAWER_FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'summary:not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])'
].join(", ");

function isExposedByDisclosures(control: HTMLElement, panel: HTMLElement): boolean {
  for (let ancestor = control.parentElement; ancestor && ancestor !== panel; ancestor = ancestor.parentElement) {
    if (ancestor.tagName !== "DETAILS" || ancestor.hasAttribute("open")) continue;
    const visibleSummary = [...ancestor.children].find((child) => child.tagName === "SUMMARY");
    if (!visibleSummary?.contains(control)) return false;
  }
  return true;
}

export function drawerFocusableControls(panel: HTMLElement): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE_SELECTOR)]
    .filter((control) => control.getClientRects().length > 0 && isExposedByDisclosures(control, panel));
}

export function installDrawerFocusBoundary(panel: HTMLElement | null, close: () => void, ownerDocument: Document = document): () => void {
  const previousActive = ownerDocument.activeElement;
  const previousFocus = previousActive && typeof (previousActive as HTMLElement).focus === "function" ? previousActive as HTMLElement : null;
  const previousBodyOverflow = ownerDocument.body.style.overflow;
  const previousRootOverflow = ownerDocument.documentElement.style.overflow;
  // WHY [BOL-DRAWER-SCROLL-001]: the task is the only active surface while
  // its modal drawer is open. Leaving the page scrollable creates two mobile
  // scrollbars and lets touch/keyboard input move hidden work behind the
  // approval. Lock both root scroll owners, preserve their exact prior inline
  // values, and let only drawer__body scroll. Falsifier: 360x668 shows two
  // scrollbars or scrolling the task moves the Work list underneath it.
  ownerDocument.body.style.overflow = "hidden";
  ownerDocument.documentElement.style.overflow = "hidden";
  const focusable = () => panel ? drawerFocusableControls(panel) : [];
  // React applies `autoFocus` during the same commit but does not guarantee
  // the HTML attribute remains queryable. Preserve that intentional field;
  // otherwise move focus into the dialog's first visible control.
  if (!panel?.contains(ownerDocument.activeElement)) (focusable()[0] ?? panel)?.focus();
  const handleKey = (event: KeyboardEvent) => {
    if (event.key === "Escape") { event.preventDefault(); close(); return; }
    if (event.key !== "Tab") return;
    const controls = focusable();
    if (!controls.length) { event.preventDefault(); panel?.focus(); return; }
    const first = controls[0]!;
    const last = controls[controls.length - 1]!;
    if (event.shiftKey && ownerDocument.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && ownerDocument.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  ownerDocument.addEventListener("keydown", handleKey);
  return () => {
    ownerDocument.removeEventListener("keydown", handleKey);
    ownerDocument.body.style.overflow = previousBodyOverflow;
    ownerDocument.documentElement.style.overflow = previousRootOverflow;
    previousFocus?.focus();
  };
}

export function closeDetailsOnEscape(event: {
  key: string;
  currentTarget: HTMLDetailsElement;
  preventDefault: () => void;
  stopPropagation: () => void;
}): void {
  if (event.key !== "Escape" || !event.currentTarget.open) return;
  // WHY [BOL-DISCLOSURE-ESCAPE-001]: a disclosure used as an action menu is
  // a temporary popup, not static reference content. Escape must dismiss it
  // and return focus to its trigger; relying on native <details> leaves the
  // Reject/Snooze menu open. Falsifier: Proposal More stays open on Escape.
  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.open = false;
  event.currentTarget.querySelector<HTMLElement>("summary")?.focus();
}

export function ToastRegion({ messages, dismiss }: { messages: readonly ToastMessage[]; dismiss: (id: number) => void }) {
  return (
    <div className="toast-region" aria-live="polite" aria-atomic="false">
      {messages.map((message) => (
        <div className={`toast toast--${message.tone}`} role="status" key={message.id}>
          {message.tone === "success" ? <CheckCircle2 size={18} aria-hidden="true" /> : <CircleAlert size={18} aria-hidden="true" />}
          <span>{message.text}</span>
          <button type="button" onClick={() => dismiss(message.id)} aria-label="Dismiss message"><X size={16} /></button>
        </div>
      ))}
    </div>
  );
}

export function Drawer({ title, eyebrow, open, close, children, footer, bodyMode = "scroll", size = "default" }: {
  title: string;
  eyebrow?: string;
  open: boolean;
  close: () => void;
  children: ReactNode;
  footer?: ReactNode;
  bodyMode?: "scroll" | "contained";
  size?: "default" | "workspace";
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef(close);
  closeRef.current = close;
  useLayoutEffect(() => {
    if (!open) return;
    // WHY [BOL-DRAWER-FOCUS-002]: native disclosure summaries and real links
    // are keyboard controls too, including the summary of a closed <details>.
    // Keep those in the modal boundary while excluding the closed body. The
    // falsifier is Tab reaching page chrome, skipping a visible summary/link,
    // or entering a closed disclosure; Escape and close must restore focus.
    return installDrawerFocusBoundary(panelRef.current, () => closeRef.current());
  }, [open]);
  if (!open) return null;
  return (
    <div className="drawer-layer">
      <button className="drawer-scrim" type="button" onClick={close} aria-label="Close panel" />
      <section ref={panelRef} className={`drawer drawer--${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="drawer__header">
          <div>
            {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={close} aria-label="Close panel"><X size={20} /></button>
        </header>
        {/* WHY [BOL-DRAWER-BODY-MODE-001]: a normal form drawer scrolls here,
            while Ask Ops owns its transcript scroll and pinned composer. Make
            that ownership explicit; inferring it with :has() can create two
            scroll owners and strand the composer below a locked viewport. */}
        <div className={`drawer__body drawer__body--${bodyMode}`}>{children}</div>
        {footer ? <footer className="drawer__footer">{footer}</footer> : null}
      </section>
    </div>
  );
}

export function Avatar({ name, size = "normal" }: { name: string; size?: "small" | "normal" }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
  return <span className={`avatar avatar--${size}`} aria-label={name} title={name}>{initials}</span>;
}

export function ColorMark({ color, className, children }: { color: string; className: string; children?: ReactNode }) {
  const markRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    markRef.current?.style.setProperty("--client-color", color);
  }, [color]);
  return <span ref={markRef} className={className} aria-hidden="true">{children}</span>;
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty-state"><span className="empty-state__mark" aria-hidden="true"><CheckCircle2 size={21} /></span><p>{children}</p></div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="field"><span className="field__label">{label}</span>{children}{hint ? <span className="field__hint">{hint}</span> : null}</label>;
}
